// Edit Message Component
// Following existing patterns from MessageComposer

import React, { useState, useRef, useCallback, useEffect } from 'react';
import { EditMessageProps } from '../../../types/messaging';
import {
  EditContainer,
  EditHeader,
  EditContent,
  EditActions,
  EditButton,
  CancelButton,
  CharacterCount
} from '../../../styles/Messaging/MessageActions.styles';

export const EditMessage: React.FC<EditMessageProps> = ({
  message,
  onSaveEdit,
  onCancel,
  disabled = false
}) => {
  const [editedText, setEditedText] = useState(message.content);
  const [isOverLimit, setIsOverLimit] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  
  const maxLength = 1000;

  useEffect(() => {
    // Focus and select all text when component mounts
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.select();
    }
  }, []);

  const handleTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setEditedText(value);
    setIsOverLimit(value.length > maxLength);
    
    // Auto-resize textarea
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, []);

  const handleSave = useCallback(async () => {
    if (!editedText.trim()) return;
    if (isOverLimit) return;
    if (disabled) return;

    try {
      await onSaveEdit(message.id, editedText.trim());
    } catch (error) {
      console.error('Failed to edit message:', error);
    }
  }, [editedText, message.id, isOverLimit, onSaveEdit, disabled]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === 'Escape') {
      onCancel();
    }
  }, [handleSave, onCancel]);

  return (
    <EditContainer>
      <EditHeader>
        <span>Edit Message</span>
        <CancelButton onClick={onCancel}>✕</CancelButton>
      </EditHeader>
      
      <EditContent>
        <textarea
          ref={textareaRef}
          value={editedText}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          rows={3}
          style={{
            width: '100%',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            padding: '0.75rem',
            fontSize: '0.9rem',
            resize: 'none',
            outline: 'none',
            fontFamily: 'inherit',
            minHeight: '80px'
          }}
        />
        
        <CharacterCount $isOverLimit={isOverLimit}>
          {editedText.length}/{maxLength}
        </CharacterCount>
      </EditContent>
      
      <EditActions>
        <CancelButton onClick={onCancel}>
          Cancel
        </CancelButton>
        <EditButton
          onClick={handleSave}
          disabled={!editedText.trim() || isOverLimit || disabled}
        >
          Save Changes
        </EditButton>
      </EditActions>
    </EditContainer>
  );
};
